import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { MODEL_CATALOG } from "@domain/model-catalog";

import { SectionHeading } from "./SectionHeading";

/**
 * The model picker, on the marketing page. Cards read straight from
 * `@domain/model-catalog` — the SAME source the settings switcher renders and
 * the Convex mutation validates against — so the lineup, the names, and the
 * relative cost can never drift from what actually ships. Add a model to the
 * catalog and it appears here; the only thing i18n owns is the framing.
 */
export function ModelsSection() {
  const { t } = useTranslation("home");

  return (
    <section
      id="models"
      className="scroll-mt-32 px-5 py-20 md:scroll-mt-24 md:py-24"
    >
      <div className="mx-auto max-w-6xl">
        <SectionHeading kicker={t("models.kicker")} title={t("models.title")} />

        <p className="mx-auto mt-6 max-w-xl text-center font-body text-[16px] leading-relaxed text-home-ink/75">
          {t("models.lede")}
        </p>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {MODEL_CATALOG.map((model, i) => (
            <motion.div
              key={model.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.4, delay: i * 0.06, ease: "easeOut" }}
              className="flex flex-col rounded-2xl border border-home-ink/10 bg-home-cream p-5"
            >
              <p className="font-body text-[11px] font-bold tracking-[0.14em] text-home-ink/65 uppercase">
                {model.provider}
              </p>
              <h3 className="mt-1 font-display text-[19px] font-semibold tracking-tight text-home-ink">
                {model.name}
              </h3>
              <p className="mt-2 flex-1 font-body text-[13.5px] leading-relaxed text-home-ink/65">
                {model.blurb}
              </p>

              {/* Context window is stated ONCE in the lede — it's identical
                  across all four, so per-card chips repeated one fact four
                  times and buried the one that actually differs. */}
              <div className="mt-4 flex flex-wrap items-center gap-1.5 border-t border-home-ink/8 pt-4">
                <span className="rounded-full bg-home-ink/[0.05] px-2.5 py-1 font-body text-[11.5px] font-semibold text-home-ink/70">
                  {t("models.effortCount", { count: model.efforts.length })}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Answers the "is four all I get / am I stuck with them?" objection
            that forms right under the cards. Deliberately promises no timeline
            — see the note in the i18n block. The per-model cost note that used
            to sit below this was cut as detail the section didn't need; the
            surprise-bill objection is still answered by FAQ qThirteen. */}
        <p className="mx-auto mt-10 max-w-2xl text-center font-body text-[16px] leading-relaxed font-medium text-home-ink/80">
          {t("models.lineup")}
        </p>
      </div>
    </section>
  );
}
